import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BellIcon, CloseIcon } from '@/components/Icons';
import { isPhone } from '@/lib/device';
import { playChime } from '@/lib/sound';

// A new alert shown inside the page while CMB Chats is open on a computer (Gary, 17 Oct).
// The computer can hide its own pop-up (Focus, notification settings, Banners that vanish),
// so the service worker also sends each alert to a Chats window on screen (sw.ts). The pop-up
// still goes. Phones are unchanged: they always get the phone's own alert.

interface Alert {
  id: number;
  title: string;
  body: string;
  url: string;
  /** A new lead or urgent: stays until clicked or closed. A reply goes after a few seconds. */
  strong: boolean;
}

const SHOW_MS = 8000;
const MAX = 3;

export function InPageAlert() {
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const next = useRef(0);
  const navigate = useNavigate();

  useEffect(() => {
    if (isPhone || !('serviceWorker' in navigator)) return;
    const timers = new Set<number>();
    const onMessage = (e: MessageEvent) => {
      const d = e.data as { type?: string; title?: string; body?: string; url?: string; strong?: boolean } | null;
      if (d?.type !== 'alert') return;
      const alert: Alert = { id: ++next.current, title: d.title ?? 'CMB Chats', body: d.body ?? '', url: d.url ?? '/', strong: !!d.strong };
      setAlerts((list) => [alert, ...list].slice(0, MAX));
      playChime();
      if (!alert.strong) {
        const t = window.setTimeout(() => {
          timers.delete(t);
          setAlerts((list) => list.filter((a) => a.id !== alert.id));
        }, SHOW_MS);
        timers.add(t);
      }
    };
    navigator.serviceWorker.addEventListener('message', onMessage);
    return () => {
      navigator.serviceWorker.removeEventListener('message', onMessage);
      timers.forEach((t) => window.clearTimeout(t));
    };
  }, []);

  if (!alerts.length) return null;
  const close = (id: number) => setAlerts((list) => list.filter((a) => a.id !== id));
  return (
    <div className="chats-inalert-stack" role="status" aria-live="polite">
      {alerts.map((a) => (
        <div key={a.id} className={`chats-inalert${a.strong ? ' chats-inalert--strong' : ''}`}>
          <button type="button" className="chats-inalert-main" onClick={() => { close(a.id); navigate(a.url); }}>
            <span className="chats-inalert-icon" aria-hidden="true"><BellIcon /></span>
            <span className="chats-inalert-text">
              <b>{a.title}</b>
              <span>{a.body}</span>
            </span>
          </button>
          <button type="button" className="chats-icon-btn chats-inalert-close" aria-label="Close" onClick={() => close(a.id)}><CloseIcon /></button>
        </div>
      ))}
    </div>
  );
}
